'use client';

import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import type { ReactNode } from 'react';

import CarTileCard from './components/CarTileCard/CarTileCard';
import CarTileContent from './components/CarTileCard/CarTileContent';
import CarTileMedia from './components/CarTileCard/CarTileMedia';
import CarTileDescription from './components/CarTileDescription/Description/CarTileDescription';
import CarTileMenu from './components/CarTileMenu/CarTileMenu';
import FavoriteButton from './components/FavoriteButton/FavoriteButton';
import BudgetCalculatorTerms from './components/Finance/BudgetCalculatorTerms/BudgetCalculatorTerms';
import FinanceTerms from './components/Finance/FinanceTerms/FinanceTerms';
import ImageGallery from './components/ImageGallery/ImageGallery';
import Badge from './components/StatusBadge/Badge';
import { buildCarTitle } from './helpers/normalizers';
import { isCarUnavailable } from './helpers/statusHelpers';
import { clickableContentSelectors } from './helpers/utilities';
import type { CalculatorInformation } from './types/CalculatorInformation';
import type { Car } from './types/Car';
import type { CompareConfig } from './types/CompareConfig';
import type { FavoritesConfig } from './types/FavoritesConfig';
import type { FinanceInformation } from './types/FinanceInformation';
import type { GalleryConfig } from './types/GalleryConfig';

export type CarTileProps = {
  car: Car;
  pageName: string;
  imageSrc: string;
  gallery?: GalleryConfig;
  compare?: CompareConfig;
  menuOptions?: ReactNode[];
  favorites?: FavoritesConfig;
  financeInformation?: FinanceInformation;
  calculatorInformation?: CalculatorInformation;
};

const CarTile = ({
  car,
  gallery,
  imageSrc,
  pageName,
  compare,
  favorites,
  menuOptions,
  financeInformation,
  calculatorInformation,
}: CarTileProps) => {
  const isUnavailable = isCarUnavailable(car.statusInfo);
  const showFavorites =
    favorites?.isFavorited !== undefined && Boolean(favorites?.onChange);

  const heroImage =
    imageSrc ||
    'https://img2.carmax.com/Images/fallback.jpg?width=400&height=300';

  const { fbsApplication, fbsDecision } = financeInformation || {};

  const hasFinanceInfo: boolean =
    !!financeInformation?.fbsApplication || !!calculatorInformation;

  const href = `/car/${car.stockNumber}`;
  const galleryEnabled = (gallery?.enabled ?? true) && !isUnavailable;
  const altText = buildCarTitle(car);

  const handleContentAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (Boolean((e.target as Element)?.matches(clickableContentSelectors))) {
      return;
    }
    window.location.href = href;
  };

  const { stockNumber, year, make, model, price } = car;
  const { isComingSoon, isReserved, isPopular, hasPriceDrop } = car.statusInfo;
  const clickProps = `Element type: Car Tile,StockNumber: ${stockNumber},YMM: ${year} ${make} ${model},Price: ${price},Coming Soon: ${isComingSoon},Reserved: ${isReserved},Marked Down: ${!!hasPriceDrop},Popular: ${isPopular}`;

  const kmxCarTileClassName = [
    'kmx-car-tile',
    isUnavailable ? 'kmx-car-tile--unavailable' : '',
    pageName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <CarTileCard
      data-id={stockNumber}
      data-ignore-anchor-tag-rule
      data-clickprops={clickProps}
      className={kmxCarTileClassName}
    >
      <CarTileMedia className="kmx-car-tile__media">
        <Badge carStatus={car.statusInfo} />
        {showFavorites && (
          <FavoriteButton
            isFavorited={favorites.isFavorited}
            onClick={favorites.onChange}
            stockNumber={stockNumber}
          />
        )}

        <CardMedia>
          {!galleryEnabled ? (
            <a href={href}>
              <img alt={altText} src={heroImage} />
            </a>
          ) : (
            <ImageGallery
              alt={altText}
              heroImageSrc={heroImage}
              href={href}
              loadAdditionalImages={gallery?.loadAdditionalImages}
              onChange={gallery?.onChange}
              onImagesLoaded={gallery?.onImagesLoaded}
              stockNumber={stockNumber}
              swiperProps={gallery?.swiperProps}
            />
          )}
        </CardMedia>
      </CarTileMedia>

      <CarTileContent
        onClick={handleContentAreaClick}
        className="kmx-car-tile__content"
      >
        {(menuOptions || compare) && !isUnavailable && (
          <CarTileMenu menuOptions={menuOptions} compareConfig={compare} />
        )}

        <CarTileDescription car={car} />

        {hasFinanceInfo && (
          <>
            <Divider sx={{ mt: 1, mb: 2 }} />

            {calculatorInformation && !fbsApplication && (
              <BudgetCalculatorTerms
                pageName={pageName}
                fbsDecision={fbsDecision}
                stockNumber={stockNumber}
                isComingSoon={!!isComingSoon}
                terms={calculatorInformation}
              />
            )}

            {financeInformation &&
              fbsApplication &&
              (fbsDecision || financeInformation.isSmallBatchLoading) && (
                <FinanceTerms
                  basePrice={price}
                  stockNumber={stockNumber}
                  carIsUnavailable={isUnavailable}
                  financeInformation={financeInformation}
                />
              )}
          </>
        )}
      </CarTileContent>
    </CarTileCard>
  );
};

export default CarTile;
